<script setup lang="ts">
import { onUnmounted, onMounted, nextTick } from 'vue'
import { VForm } from 'vuetify/components/VForm'
import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2LoginIllustrationBorderedDark from '@images/pages/auth-v2-login-illustration-bordered-dark.png'
import authV2LoginIllustrationBorderedLight from '@images/pages/auth-v2-login-illustration-bordered-light.png'
import authV2LoginIllustrationDark from '@images/pages/auth-v2-login-illustration-dark.png'
import authV2LoginIllustrationLight from '@images/pages/auth-v2-login-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { ensureImpersonatedIdentity } from '@/composables/useImpersonation'
import { userAbilityRulesStorage } from '@layouts/plugins/casl'
import { themeConfig } from '@themeConfig'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const form = ref({
  compNo: useStorage('compNo').value,
  username: '',
  password: '',
  remember: false,
})

const isPasswordVisible = ref(false)
const route = useRoute()
const router = useRouter()

const provider = route.query.provider
const oauthLogin = route.query.oauthLogin
const localLogin = route.query.localLogin

// 開發模式（npm run dev）一律走本機帳密登入，不自動導向 OAuth SSO
const isDev = import.meta.env.DEV

const ability = useAbility()

const authThemeImg = useGenerateImageVariant(
  authV2LoginIllustrationLight,
  authV2LoginIllustrationDark,
  authV2LoginIllustrationBorderedLight,
  authV2LoginIllustrationBorderedDark,
  true)

const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

const refVForm = ref<VForm>()

const rememberMe = ref(false)
const isLoading = ref(false)
const loadingMessage = ref('登入中請稍候...')
let loadingMessageTimer: ReturnType<typeof setTimeout> | null = null
let isMessageSwitching = false

// 動態載入訊息列表
const loadingMessages = [
  '正在驗證帳號密碼...',
  '正在連線伺服器...',
  '正在載入使用者資料...',
  '正在檢查權限設定...',
  '正在初始化系統環境...',
  '即將完成登入...',
]

// 開始動態訊息切換（依序顯示各一次）
const startLoadingMessages = () => {
  if (isMessageSwitching) return // 如果已經在切換，則不重複啟動
  
  isMessageSwitching = true
  // 從第一個訊息開始
  let messageIndex = 0
  loadingMessage.value = loadingMessages[messageIndex]
  
  const switchMessage = () => {
    if (!isMessageSwitching) return // 如果已停止，則不再切換
    
    // 移到下一個訊息
    messageIndex = messageIndex + 1
    
    // 如果已經到達最後一個訊息，就停止切換
    if (messageIndex >= loadingMessages.length) {
      return // 停止循環，保持在最後一個訊息
    }
    
    loadingMessage.value = loadingMessages[messageIndex]
    
    // 隨機時間間隔：5 秒到 10 秒之間
    const randomDelay = 5000 + Math.random() * 5000
    loadingMessageTimer = setTimeout(switchMessage, randomDelay)
  }
  
  // 第一次切換使用隨機延遲（較短，讓用戶更快看到變化）
  const initialDelay = 2000 + Math.random() * 1500
  loadingMessageTimer = setTimeout(switchMessage, initialDelay)
}

// 停止動態訊息切換
const stopLoadingMessages = () => {
  isMessageSwitching = false
  if (loadingMessageTimer) {
    clearTimeout(loadingMessageTimer)
    loadingMessageTimer = null
  }
  loadingMessage.value = '登入中請稍候...'
}

const errors = ref<Record<string, string | undefined>>({
  userId: undefined,
  password: undefined,
})

const oauth2Enable = ref(useStorage('oauth2Enable').value)

// 將 OAuth 登入邏輯移到 onMounted，確保組件已掛載
onMounted(async () => {
  try {
    if (oauthLogin == 'Y') {
      // 登入前先清空舊的本機儲存空間，避免舊資料干擾
      localStorage.clear()
      
      // 使用 nextTick 確保 DOM 已更新，VOverlay 已渲染
      await nextTick()
      isLoading.value = true
      startLoadingMessages()

      const userRes = await $api('/sys/user/oauthUser?provider=' + provider, {
        method: 'GET',
        onResponseError({ response }) {
          errors.value = response._data.errors
          stopLoadingMessages()
          isLoading.value = false // 發生錯誤時關閉載入狀態
        },
      })
   
      stopLoadingMessages()
      loadingMessage.value = '正在處理登入資料...'
      
      const { data } = userRes
      console.debug('userRes data: ', data)
      let resetPassword = data.userInfo.initPasswd

      if(resetPassword){
        loadingMessage.value = '需要重設密碼，即將跳轉...'
        await nextTick(() => {
          router.replace('/reset-passwd?provider='+provider+'&userId='+data.userInfo.userno+'&to='+(route.query.to ? String(route.query.to) : '/'))
        })
      }else{
        // 使用共享的 ref，確保與 isHidden 函數同步
        userAbilityRulesStorage.value = data.userAbilityRules
        ability.update(data.userAbilityRules)

        useStorage('userData', {}).value = data.userInfo
        useStorage('roles', []).value = data.userInfo.roles
        useStorage('userName', '').value = data.userInfo.name
        useCookie('userToken').value = data.userInfo.token
        useStorage('orgType', '').value = data.userInfo.orgType
        await ensureImpersonatedIdentity()
        ability.update(userAbilityRulesStorage.value)

        loadingMessage.value = '即將跳轉頁面...'
        console.log('OAuth Login Success, target:', route.query.to || '/tca')
       // Redirect to `to` query if exist or redirect to index route
       // ❗ nextTick is required to wait for DOM updates and later redirect
        await nextTick(() => {
          router.replace(route.query.to ? String(route.query.to) : '/tca')
        })
      }
    } else if (oauth2Enable.value == 'true' && localLogin != 'Y' && !isDev) {
      // OAuth2 啟用時，自動導向 SSO 登入頁（從 script setup 移至此處，確保 DOM 就緒後再導航）
      const toParam = route.query.to ? `&to=${route.query.to}` : ''
      window.location.href = `/sys/user/oauth?provider=nstc${toParam}`
    }
  } catch(error) {
    console.log('error: ', error)
    stopLoadingMessages()
    isLoading.value = false // 發生異常時關閉載入狀態
  }
})

const errorMsg = ref('')

const login = async () => {
  try {
    // 登入前先清空舊的本機儲存空間，避免舊資料干擾
    localStorage.clear()

    // 開始顯示載入狀態和動態訊息
    isLoading.value = true
    errorMsg.value = '' // 清除之前的錯誤訊息
    // 確保 DOM 已更新，loading 畫面能立即顯示
    await nextTick()
    startLoadingMessages()
    
    const res = await $api('/sys/user/login', {
      method: 'POST',
      body: {
        compNo: form.value.compNo,
        username: form.value.username.toUpperCase(),
        password: form.value.password,
      },
      onResponseError({ response }) {
        errorMsg.value = response._data.message
        stopLoadingMessages()
        isLoading.value = false // 發生錯誤時關閉載入狀態
      },
    })

    // API 回應後停止動態訊息，顯示固定訊息
    stopLoadingMessages()
    loadingMessage.value = '正在處理登入資料...'

    const { data } = res
    console.debug('login res data: ', data)
    //
    // 使用共享的 ref，確保與 isHidden 函數同步
    userAbilityRulesStorage.value = data.userAbilityRules
    ability.update(data.userAbilityRules)
    //
    useStorage('userData', {}).value = data.userInfo
    useStorage('roles', []).value = data.userInfo.roles
    useStorage('userName', '').value = data.userInfo.name
    useCookie('userToken').value = data.userInfo.token
    useStorage('depNo', '').value = data.userInfo.depNo
    useStorage('orgType', '').value = data.userInfo.orgType
    await ensureImpersonatedIdentity()
    ability.update(userAbilityRulesStorage.value)
    // useCookie('accessToken').value = accessToken
    //
    loadingMessage.value = '即將跳轉頁面...'
    console.log('Manual Login Success, target:', route.query.to || '/tca')
    // // Redirect to `to` query if exist or redirect to index route
    // // ❗ nextTick is required to wait for DOM updates and later redirect
    await nextTick(() => {
      router.replace(route.query.to ? String(route.query.to) : '/tca')
    })
    // 注意：成功時不需要關閉 isLoading，因為會直接跳轉頁面
  }
  catch (err) {
    console.error(err)
    stopLoadingMessages()
    isLoading.value = false // 發生異常時關閉載入狀態
  }
}

const onSubmit = () => {
  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid)
        login()
    })
}

// 組件卸載時清理定時器
onUnmounted(() => {
  stopLoadingMessages()
})
</script>

<template>
  <RouterLink v-if="isDev || (oauth2Enable != 'true' && oauthLogin != 'Y') || localLogin == 'Y'" to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>
  <VOverlay
    v-model="isLoading"
    persistent
    class="d-flex align-center justify-center"
    scrim="rgba(0, 0, 0, 0.7)"
  >
    <VCard
      class="pa-8"
      max-width="400"
      elevation="8"
    >
      <div class="d-flex flex-column align-center text-center">
        <VProgressCircular 
          indeterminate 
          size="64" 
          color="primary"
          class="mb-4"
        />
        <div class="text-h6 text-primary mb-2">{{ loadingMessage }}</div>
        <div class="text-body-2 text-medium-emphasis">
          請勿關閉此視窗
        </div>
      </div>
    </VCard>
  </VOverlay>
  <VRow
    v-if="isDev || (oauth2Enable != 'true' && oauthLogin != 'Y') || localLogin == 'Y'"
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 6.25rem;"
        >
          <VImg
            max-width="613"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            <span class="text-capitalize"> 歡迎來到 NSTC 管理系統</span>
          </h4>
          <p class="mb-0">
            請先登入您的帳戶
          </p>
        </VCardText>
<!--        <VCardText>-->
<!--          <VAlert-->
<!--            color="primary"-->
<!--            variant="tonal"-->
<!--          >-->
<!--            <p class="text-sm mb-2">-->
<!--              Admin Email: <strong>admin@demo.com</strong> / Pass: <strong>admin</strong>-->
<!--            </p>-->
<!--            <p class="text-sm mb-0">-->
<!--              Client Email: <strong>client@demo.com</strong> / Pass: <strong>client</strong>-->
<!--            </p>-->
<!--          </VAlert>-->
<!--        </VCardText>-->
        <VAlert
          class="text-center"
          variant="outlined"
          color="error"
          v-if="errorMsg != ''"
        >
          {{ errorMsg }}
        </VAlert>
        <VCardText>
          <VForm ref="refVForm"
                 @submit.prevent="onSubmit"
          >
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.username"
                  autofocus
                  label="帳號/中心員編"
                  type="text"
                  placeholder="IX0000"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  label="密碼"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="off"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center flex-wrap justify-space-between mt-2 mb-4">
                  <VCheckbox
                    v-model="form.remember"
                    label="記住帳號"
                  />
                  <RouterLink
                    class="text-primary ms-1"
                    :to="{ name: 'forgot-password' }"
                  >
                    忘記密碼?
                  </RouterLink>
                </div>

                <VBtn
                  block
                  type="submit"
                >
                  登入
                </VBtn>
              </VCol>

<!--               create account-->
              <VCol
                cols="12"
                class="text-center"
              >
<!--                <span>New on our platform?</span>-->

                <RouterLink
                  class="text-primary ms-1"
                  :to="{ name: 'register' }"
                >
                  註冊帳號
                </RouterLink>
              </VCol>
              <VCol
                cols="12"
                class="d-flex align-center"
              >
                <VDivider />
                <span class="mx-4">or</span>
                <VDivider />
              </VCol>

              <!-- auth providers -->
              <VCol
                cols="12"
                class="text-center"
              >
                <AuthProvider />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
